import { apiClient } from '../../api/client';
import { ENDPOINTS } from '../../api/endpoints';
import { Role, Permission, MembershipPermissions, resolvePermissions } from '../permissions/permissions.service';

function unwrap(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

async function requestWithRetry<T>(request: () => Promise<T>, attempts = 4): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await request();
    } catch (error: any) {
      lastError = error;
      const status = error?.status || error?.response?.status;
      if (status !== 429 || attempt === attempts - 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 8000)));
    }
  }
  throw lastError;
}

export class WorkspaceService {
  static async list(params?: Record<string, any>): Promise<any[]> {
    const response = await requestWithRetry(() => apiClient.get(ENDPOINTS.WORKSPACE.LIST, { params }));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  }

  static async listAll(): Promise<any[]> {
    const response = await requestWithRetry(() => apiClient.get(ENDPOINTS.WORKSPACE.LIST_ALL));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  }

  static async getById(id: string | number): Promise<any> {
    const response = await apiClient.get(ENDPOINTS.WORKSPACE.DETAILS(id));
    return unwrap(response.data);
  }

  static async create(data: any): Promise<any> {
    const response = await apiClient.post(ENDPOINTS.WORKSPACE.CREATE, data);
    return unwrap(response.data);
  }

  static async update(id: string | number, data: any): Promise<any> {
    const response = await apiClient.put(ENDPOINTS.WORKSPACE.UPDATE(id), data);
    return unwrap(response.data);
  }

  static async delete(id: string | number): Promise<void> {
    await apiClient.delete(ENDPOINTS.WORKSPACE.DELETE(id));
  }

  static async getMembers(id: string | number): Promise<any[]> {
    const response = await apiClient.get(ENDPOINTS.WORKSPACE.MEMBERS(id));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  }

  /**
   * Fetch the current user's membership for a workspace.
   * BACKEND DEPENDENCY: GET /api/workspaces/{id}/membership
   * Returns { role, permissions } for the authenticated user in that workspace.
   */
  static async getMembership(id: string | number): Promise<MembershipPermissions | null> {
    try {
      const response = await apiClient.get(ENDPOINTS.WORKSPACE.MEMBERSHIP(id));
      const unwrapped = unwrap(response.data);
      return unwrapped as MembershipPermissions;
    } catch (err) {
      if (err instanceof Error) {
        // 404 = user has no membership yet; fall back to defaults
      }
      return null;
    }
  }

  /**
   * Resolve the effective membership for a workspace.
   * If API membership is unavailable, derive from user's global role.
   */
  static async resolveMembership(
    workspaceId: string | number,
    globalUser: any,
  ): Promise<MembershipPermissions> {
    const apiMembership = await this.getMembership(workspaceId);
    if (apiMembership) {
      return apiMembership;
    }

    // Fallback: derive role from user object if API membership unavailable
    const fallbackRole: Role = (globalUser?.role
      ? normalizeFallbackRole(globalUser.role)
      : 'VIEWER');
    return {
      role: fallbackRole,
      permissions: resolvePermissions({ role: fallbackRole, permissions: [] }),
    };
  }

  /**
   * Fetch all roles defined for a workspace (for permissions management).
   * BACKEND DEPENDENCY: GET /api/workspaces/{id}/roles
   */
  static async getRoles(id: string | number): Promise<any[]> {
    const response = await apiClient.get(ENDPOINTS.WORKSPACE.ROLES(id));
    const unwrapped = unwrap(response.data);
    return Array.isArray(unwrapped) ? unwrapped : [];
  }
}

function normalizeFallbackRole(raw: string): Role {
  const upper = raw.toUpperCase().trim();
  const roleMap: Record<string, Role> = {
    OWNER: 'OWNER',
    ADMIN: 'ADMIN',
    ADMINISTRATOR: 'ADMIN',
    MANAGER: 'MANAGER',
    MEMBER: 'MEMBER',
    USER: 'MEMBER',
    VIEWER: 'VIEWER',
  };
  return roleMap[upper] ?? 'VIEWER';
}